import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, PencilSimple, MagnifyingGlass, ArrowLeft, FloppyDisk, Table as TableIcon, Printer } from "@phosphor-icons/react";
import { toast } from "sonner";
import Pagination from "@/components/Pagination";
import ExportButtons from "@/components/ExportButtons";
import { exportToExcel, exportToPDF } from "@/lib/export";
import { useAuth } from "@/contexts/AuthContext";

const FIELD_ROLES = ["asisten_supervisor", "korsub", "korwil"];

const STATUS_OPTIONS = [
  { value: "persiapan", label: "Persiapan", color: "bg-slate-100 text-slate-700" },
  { value: "tebar", label: "Tebar", color: "bg-blue-100 text-blue-700" },
  { value: "pemeliharaan", label: "Pemeliharaan", color: "bg-emerald-100 text-emerald-700" },
  { value: "panen", label: "Panen", color: "bg-amber-100 text-amber-700" },
  { value: "selesai", label: "Selesai", color: "bg-purple-100 text-purple-700" },
];

const SIKLUS_COLS = [
  { header: "Tambak", accessor: (r) => r.tambak_nama },
  { header: "Tgl Tebar", accessor: (r) => r.tanggal_tebar },
  { header: "Jumlah Benur", accessor: (r) => r.jumlah_benur },
  { header: "Asal Benur", accessor: (r) => r.asal_benur },
  { header: "Status", accessor: (r) => r.status },
  { header: "Dibuat Oleh", accessor: (r) => r.created_by },
];

const MON_COLS = [
  { header: "No", accessor: (r) => r.no },
  { header: "Tanggal", accessor: (r) => r.tanggal || "" },
  { header: "DOC", accessor: (r) => r.doc },
  { header: "Pakan 07:00", accessor: (r) => r.pakan_0700 ?? "" },
  { header: "Pakan 12:00", accessor: (r) => r.pakan_1200 ?? "" },
  { header: "Pakan 17:00", accessor: (r) => r.pakan_1700 ?? "" },
  { header: "Pakan 22:00", accessor: (r) => r.pakan_2200 ?? "" },
  { header: "Pakan/HR", accessor: (r) => r.pakan_harian ?? "" },
  { header: "Kumulatif", accessor: (r) => r.pakan_kumulatif ?? "" },
  { header: "Obat", accessor: (r) => r.obat ?? "" },
  { header: "Mengisi Air", accessor: (r) => r.mengisi_air ?? "" },
  { header: "Tinggi Air", accessor: (r) => r.tinggi_air ?? "" },
  { header: "Kecerahan Pagi", accessor: (r) => r.kecerahan_pagi ?? "" },
  { header: "Kecerahan Siang", accessor: (r) => r.kecerahan_siang ?? "" },
  { header: "Kecerahan Sore", accessor: (r) => r.kecerahan_sore ?? "" },
  { header: "Anco Habis", accessor: (r) => r.anco_habis ? "Ya" : r.anco_habis === false ? "Tidak" : "" },
  { header: "Anco Tidak", accessor: (r) => r.anco_tidak ? "Ya" : r.anco_tidak === false ? "Tidak" : "" },
];

export default function SiklusPage() {
  const { user } = useAuth();
  const isFieldRole = FIELD_ROLES.includes(user?.role);
  const [items, setItems] = useState([]);
  const [tambakList, setTambakList] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({
    tambak_id: "", status: "persiapan",
    // Persiapan
    tanggal_persiapan: "", kegiatan_persiapan: "", kondisi_tambak: "",
    // Tebar
    tanggal_tebar: "", jumlah_benur: 0, asal_benur: "", ukuran_benur: "", harga_benur: null,
    // Pemeliharaan
    target_size: null, target_hari: null, metode_budidaya: "",
    // Panen
    tanggal_panen: "", estimasi_berat: null, estimasi_size: null,
    // Umum
    catatan: ""
  });
  // Monitoring state
  const [monitoringView, setMonitoringView] = useState(null);
  const [monitoringData, setMonitoringData] = useState([]);
  const [monitoringSiklus, setMonitoringSiklus] = useState(null);
  const [monLoading, setMonLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadSiklus = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([
        api.get(`/siklus?page=${page}&limit=15&search=${search}`),
        api.get("/tambak?limit=500")
      ]);
      setItems(s.data.data);
      setTotal(s.data.total);
      setTambakList(t.data.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [page, search]);

  useEffect(() => { loadSiklus(); }, [loadSiklus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) {
        await api.put(`/siklus/${editItem._id}`, form);
        toast.success("Siklus berhasil diperbarui");
      } else {
        await api.post("/siklus", form);
        toast.success("Siklus berhasil ditambahkan");
      }
      setDialogOpen(false);
      setEditItem(null);
      setForm({ tambak_id: "", tanggal_tebar: "", jumlah_benur: 0, asal_benur: "", status: "persiapan", target_size: null, target_hari: null, catatan: "" });
      loadSiklus();
    } catch (err) { toast.error(err.response?.data?.detail || "Gagal menyimpan"); }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setForm({
      tambak_id: item.tambak_id, status: item.status,
      tanggal_persiapan: item.tanggal_persiapan || "", kegiatan_persiapan: item.kegiatan_persiapan || "", kondisi_tambak: item.kondisi_tambak || "",
      tanggal_tebar: item.tanggal_tebar || "", jumlah_benur: item.jumlah_benur || 0, asal_benur: item.asal_benur || "", ukuran_benur: item.ukuran_benur || "", harga_benur: item.harga_benur || null,
      target_size: item.target_size, target_hari: item.target_hari, metode_budidaya: item.metode_budidaya || "",
      tanggal_panen: item.tanggal_panen || "", estimasi_berat: item.estimasi_berat || null, estimasi_size: item.estimasi_size || null,
      catatan: item.catatan || ""
    });
    setDialogOpen(true);
  };

  // ---- Monitoring Functions ----
  const openMonitoring = async (siklus) => {
    setMonitoringView(siklus._id);
    setMonitoringSiklus(siklus);
    setMonLoading(true);
    try {
      // Init monitoring if not exists
      await api.post(`/siklus/${siklus._id}/init-monitoring`);
      const { data } = await api.get(`/siklus/${siklus._id}/monitoring`);
      setMonitoringData(data.data);
      if (data.siklus) setMonitoringSiklus(data.siklus);
    } catch (err) { toast.error("Gagal memuat monitoring"); }
    setMonLoading(false);
  };

  const reloadMonitoring = async () => {
    if (!monitoringView) return;
    try {
      const { data } = await api.get(`/siklus/${monitoringView}/monitoring`);
      setMonitoringData(data.data);
    } catch { /* ignore */ }
  };

  const startEditing = (row) => {
    setEditingRow(row._id);
    setEditForm({
      pakan_0700: row.pakan_0700 ?? "",
      pakan_1200: row.pakan_1200 ?? "",
      pakan_1700: row.pakan_1700 ?? "",
      pakan_2200: row.pakan_2200 ?? "",
      obat: row.obat ?? "",
      mengisi_air: row.mengisi_air ?? "",
      tinggi_air: row.tinggi_air ?? "",
      kecerahan_pagi: row.kecerahan_pagi ?? "",
      kecerahan_siang: row.kecerahan_siang ?? "",
      kecerahan_sore: row.kecerahan_sore ?? "",
      anco_habis: row.anco_habis ?? false,
      anco_tidak: row.anco_tidak ?? false,
      keterangan: row.keterangan ?? "",
    });
  };

  const saveRow = async () => {
    if (!editingRow) return;
    try {
      const payload = { ...editForm };
      ["pakan_0700", "pakan_1200", "pakan_1700", "pakan_2200", "tinggi_air", "kecerahan_pagi", "kecerahan_siang", "kecerahan_sore"].forEach((k) => {
        payload[k] = payload[k] === "" ? null : parseFloat(payload[k]) || null;
      });
      await api.put(`/monitoring/${editingRow}`, payload);
      toast.success("Data monitoring disimpan");
      setEditingRow(null);
      reloadMonitoring();
    } catch { toast.error("Gagal menyimpan"); }
  };

  const statusBadge = (s) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === s);
    return <Badge className={`${opt?.color || ""} hover:${opt?.color || ""}`}>{opt?.label || s}</Badge>;
  };

  const hasData = (row) => row.pakan_0700 != null || row.pakan_1200 != null || row.obat || row.tinggi_air != null;

  const handlePrint = () => {
    const s = monitoringSiklus;
    const filled = monitoringData.filter(hasData);
    const totalPakan = monitoringData[monitoringData.length - 1]?.pakan_kumulatif || 0;
    const rows = monitoringData.map((r) => `
      <tr style="${hasData(r) ? '' : 'color:#bbb;'}">
        <td>${r.no}</td><td>${r.tanggal?.slice(5) || '-'}</td><td><b>${r.doc}</b></td>
        <td>${r.pakan_0700 ?? '-'}</td><td>${r.pakan_1200 ?? '-'}</td><td>${r.pakan_1700 ?? '-'}</td><td>${r.pakan_2200 ?? '-'}</td>
        <td><b>${r.pakan_harian ?? '-'}</b></td><td>${r.pakan_kumulatif ?? '-'}</td>
        <td>${r.obat || '-'}</td><td>${r.mengisi_air || '-'}</td><td>${r.tinggi_air ?? '-'}</td>
        <td>${r.kecerahan_pagi ?? '-'}</td><td>${r.kecerahan_siang ?? '-'}</td><td>${r.kecerahan_sore ?? '-'}</td>
        <td>${r.anco_habis ? 'V' : '-'}</td><td>${r.anco_tidak ? 'V' : '-'}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><title>Monitoring Harian - ${s?.tambak_nama}</title>
      <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family:'IBM Plex Sans',Arial,sans-serif; font-size:9px; padding:12mm; color:#1A1A1A; }
        h1 { font-family:'Work Sans',sans-serif; font-size:16px; text-align:center; margin-bottom:4px; }
        .subtitle { text-align:center; font-size:10px; color:#666; margin-bottom:12px; }
        .info { display:flex; gap:20px; margin-bottom:10px; font-size:10px; }
        .info span { font-weight:600; }
        table { width:100%; border-collapse:collapse; font-size:8px; }
        th, td { border:1px solid #ccc; padding:2px 3px; text-align:center; }
        th { background:#B8860B; color:white; font-weight:600; font-size:7px; text-transform:uppercase; }
        td:first-child { font-weight:600; width:25px; }
        tr:nth-child(even) { background:#f9f9f9; }
        .footer { margin-top:12px; font-size:9px; color:#999; text-align:center; }
        @media print { body { padding:5mm; } @page { size:landscape; margin:5mm; } }
      </style></head><body>
      <h1>MONITORING HARIAN P3UW</h1>
      <p class="subtitle">${s?.tambak_nama || ''} - Siklus ${s?.tanggal_tebar || ''}</p>
      <div class="info">
        <div>Tanggal Tebar: <span>${s?.tanggal_tebar || '-'}</span></div>
        <div>Jumlah Benur: <span>${(s?.jumlah_benur || 0).toLocaleString('id-ID')}</span></div>
        <div>Asal Benur: <span>${s?.asal_benur || '-'}</span></div>
        <div>Hari Terisi: <span>${filled.length}/120</span></div>
        <div>Total Pakan: <span>${totalPakan} kg</span></div>
      </div>
      <table>
        <thead><tr>
          <th>No</th><th>Tgl</th><th>DOC</th>
          <th>07:00</th><th>12:00</th><th>17:00</th><th>22:00</th>
          <th>Total</th><th>Kumul.</th>
          <th>Obat</th><th>Isi Air</th><th>T.Air</th>
          <th>Kec.Pagi</th><th>Kec.Siang</th><th>Kec.Sore</th>
          <th>Habis</th><th>Tidak</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p class="footer">Dicetak dari Dashboard P3UW - KPBD Bumi Dipasena - ${new Date().toLocaleDateString('id-ID', { day:'numeric', month:'long', year:'numeric' })}</p>
      </body></html>`;
    const win = window.open('', '_blank');
    if (!win) { toast.error("Popup diblokir. Aktifkan popup untuk mencetak."); return; }
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  };

  // ---- MONITORING VIEW ----
  if (monitoringView) {
    return (
      <div data-testid="monitoring-view" className="space-y-4">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setMonitoringView(null); setEditingRow(null); }} data-testid="back-to-siklus" className="h-8 w-8 p-0">
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>
                Monitoring Harian
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">{monitoringSiklus?.tambak_nama} &mdash; Tebar: {monitoringSiklus?.tanggal_tebar} &mdash; Benur: {monitoringSiklus?.jumlah_benur?.toLocaleString("id-ID")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handlePrint} data-testid="print-monitoring-btn" className="border-slate-300 text-slate-600">
              <Printer size={16} className="mr-1.5" /> Cetak
            </Button>
            <ExportButtons
              onExcel={() => exportToExcel(monitoringData, MON_COLS, `monitoring_${monitoringSiklus?.tambak_nama || "siklus"}`)}
              onPDF={() => exportToPDF(monitoringData, MON_COLS, `monitoring_${monitoringSiklus?.tambak_nama || "siklus"}`, `Monitoring Harian - ${monitoringSiklus?.tambak_nama || ""}`)}
            />
          </div>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Card className="bg-white border border-slate-200"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Status</p>
            <div className="mt-1">{statusBadge(monitoringSiklus?.status)}</div>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Hari Terisi</p>
            <p className="text-lg font-semibold text-[#1A1A1A] mt-0.5" style={{ fontFamily: "Work Sans" }}>{monitoringData.filter(hasData).length}/120</p>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Total Pakan</p>
            <p className="text-lg font-semibold text-[#B8860B] mt-0.5" style={{ fontFamily: "Work Sans" }}>{(monitoringData[monitoringData.length - 1]?.pakan_kumulatif || 0).toLocaleString("id-ID")} kg</p>
          </CardContent></Card>
          <Card className="bg-white border border-slate-200"><CardContent className="p-3">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Asal Benur</p>
            <p className="text-sm font-medium text-[#1A1A1A] mt-0.5 truncate">{monitoringSiklus?.asal_benur}</p>
          </CardContent></Card>
        </div>

        {/* Monitoring Table */}
        <Card className="bg-white border border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#B8860B]/5">
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold w-10 sticky left-0 bg-[#B8860B]/5 z-10">No</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold w-24">Tanggal</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold w-12">DOC</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-center" colSpan={4}>Pakan (kg)</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-center">Total</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-center">Kumul.</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold">Obat</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold">Isi Air</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold">T. Air</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-center" colSpan={3}>Kecerahan</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold text-center" colSpan={2}>Anco</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider font-bold w-10">Aksi</TableHead>
                  </TableRow>
                  <TableRow className="bg-slate-50">
                    <TableHead className="sticky left-0 bg-slate-50 z-10" />
                    <TableHead />
                    <TableHead />
                    <TableHead className="text-[9px] text-center text-slate-500 px-1">07:00</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500 px-1">12:00</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500 px-1">17:00</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500 px-1">22:00</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">HR</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Total</TableHead>
                    <TableHead />
                    <TableHead />
                    <TableHead className="text-[9px] text-slate-500">(cm)</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Pagi</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Siang</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Sore</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Habis</TableHead>
                    <TableHead className="text-[9px] text-center text-slate-500">Tidak</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monLoading ? (
                    <TableRow><TableCell colSpan={18} className="text-center py-8 text-slate-400">Memuat data monitoring...</TableCell></TableRow>
                  ) : monitoringData.map((row) => {
                    const isEditing = editingRow === row._id;
                    const filled = hasData(row);
                    return (
                      <TableRow key={row._id} className={`${filled ? "bg-white" : "bg-slate-50/50"} hover:bg-[#B8860B]/[0.03] transition-colors`}>
                        <TableCell className="text-xs font-mono font-semibold text-slate-600 sticky left-0 bg-inherit z-10">{row.no}</TableCell>
                        <TableCell className="text-xs text-slate-500">{row.tanggal?.slice(5) || "-"}</TableCell>
                        <TableCell className="text-xs font-semibold">{row.doc}</TableCell>
                        {isEditing ? (
                          <>
                            {["pakan_0700", "pakan_1200", "pakan_1700", "pakan_2200"].map((k) => (
                              <TableCell key={k} className="px-0.5">
                                <Input type="number" step="0.1" className="h-7 w-14 text-xs text-center px-1" value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} />
                              </TableCell>
                            ))}
                            <TableCell className="text-xs text-center font-semibold text-[#B8860B]">
                              {(parseFloat(editForm.pakan_0700) || 0) + (parseFloat(editForm.pakan_1200) || 0) + (parseFloat(editForm.pakan_1700) || 0) + (parseFloat(editForm.pakan_2200) || 0) || "-"}
                            </TableCell>
                            <TableCell className="text-xs text-center text-slate-400">{row.pakan_kumulatif ?? "-"}</TableCell>
                            <TableCell className="px-0.5"><Input className="h-7 w-16 text-xs px-1" value={editForm.obat} onChange={(e) => setEditForm({ ...editForm, obat: e.target.value })} /></TableCell>
                            <TableCell className="px-0.5"><Input className="h-7 w-14 text-xs px-1" value={editForm.mengisi_air} onChange={(e) => setEditForm({ ...editForm, mengisi_air: e.target.value })} /></TableCell>
                            <TableCell className="px-0.5"><Input type="number" step="0.1" className="h-7 w-14 text-xs px-1" value={editForm.tinggi_air} onChange={(e) => setEditForm({ ...editForm, tinggi_air: e.target.value })} /></TableCell>
                            {["kecerahan_pagi", "kecerahan_siang", "kecerahan_sore"].map((k) => (
                              <TableCell key={k} className="px-0.5"><Input type="number" step="0.1" className="h-7 w-12 text-xs text-center px-0.5" value={editForm[k]} onChange={(e) => setEditForm({ ...editForm, [k]: e.target.value })} /></TableCell>
                            ))}
                            <TableCell className="text-center px-0.5"><Checkbox checked={editForm.anco_habis} onCheckedChange={(v) => setEditForm({ ...editForm, anco_habis: v, anco_tidak: !v })} /></TableCell>
                            <TableCell className="text-center px-0.5"><Checkbox checked={editForm.anco_tidak} onCheckedChange={(v) => setEditForm({ ...editForm, anco_tidak: v, anco_habis: !v })} /></TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={saveRow} className="h-7 w-7 p-0 text-[#008080]" data-testid={`save-mon-${row._id}`}>
                                <FloppyDisk size={14} weight="bold" />
                              </Button>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell className="text-xs text-center">{row.pakan_0700 ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.pakan_1200 ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.pakan_1700 ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.pakan_2200 ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center font-semibold text-[#B8860B]">{row.pakan_harian ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center text-slate-500">{row.pakan_kumulatif ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs truncate max-w-[60px]">{row.obat || <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs truncate max-w-[50px]">{row.mengisi_air || <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.tinggi_air ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.kecerahan_pagi ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.kecerahan_siang ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.kecerahan_sore ?? <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.anco_habis ? "V" : <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell className="text-xs text-center">{row.anco_tidak ? "V" : <span className="text-slate-300">-</span>}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="ghost" onClick={() => startEditing(row)} className="h-7 w-7 p-0" data-testid={`edit-mon-${row._id}`}>
                                <PencilSimple size={13} />
                              </Button>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- SIKLUS LIST VIEW ----
  return (
    <div data-testid="siklus-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold text-[#1A1A1A] tracking-tight" style={{ fontFamily: "Work Sans" }}>Siklus Budidaya</h1>
          <p className="text-sm text-slate-500 mt-1">{isFieldRole ? "Pilih siklus lalu isi monitoring harian" : "Kelola siklus budidaya udang per tambak"}</p>
        </div>
        <div className="flex items-center gap-2">
          {!isFieldRole && <ExportButtons onExcel={() => exportToExcel(items, SIKLUS_COLS, "siklus_budidaya")} onPDF={() => exportToPDF(items, SIKLUS_COLS, "siklus_budidaya", "Siklus Budidaya P3UW")} />}
          <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditItem(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-siklus-btn" className="bg-[#008080] hover:bg-[#006B6B] text-white"><Plus size={18} className="mr-2" /> Siklus Baru</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader><DialogTitle style={{ fontFamily: "Work Sans" }}>{editItem ? "Edit Siklus" : "Siklus Baru"}</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Tambak + Status (selalu tampil) */}
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tambak</Label>
                  <Select value={form.tambak_id} onValueChange={(v) => setForm({ ...form, tambak_id: v })}><SelectTrigger data-testid="select-siklus-tambak"><SelectValue placeholder="Pilih Tambak" /></SelectTrigger><SelectContent>{tambakList.map((t) => <SelectItem key={t._id} value={t._id}>{t.kode} - {t.nama}</SelectItem>)}</SelectContent></Select></div>
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger data-testid="select-siklus-status"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>

                {/* Status hint */}
                <div className={`p-3 rounded-lg text-xs ${
                  form.status === "persiapan" ? "bg-slate-50 border border-slate-200 text-slate-600" :
                  form.status === "tebar" ? "bg-blue-50 border border-blue-200 text-blue-700" :
                  form.status === "pemeliharaan" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" :
                  form.status === "panen" ? "bg-amber-50 border border-amber-200 text-amber-700" :
                  "bg-purple-50 border border-purple-200 text-purple-700"
                }`}>
                  {form.status === "persiapan" && "Tahap persiapan kolam sebelum tebar benur (pengeringan, pengapuran, pengisian air)"}
                  {form.status === "tebar" && "Tahap penebaran benur udang ke kolam tambak"}
                  {form.status === "pemeliharaan" && "Tahap pemeliharaan harian (pakan, monitoring air, sampling)"}
                  {form.status === "panen" && "Tahap panen udang dari kolam tambak"}
                  {form.status === "selesai" && "Siklus budidaya telah selesai, evaluasi hasil"}
                </div>

                {/* === PERSIAPAN === */}
                {form.status === "persiapan" && (<>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Mulai Persiapan</Label>
                    <Input data-testid="input-tanggal-persiapan" type="date" value={form.tanggal_persiapan} onChange={(e) => setForm({ ...form, tanggal_persiapan: e.target.value })} required /></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kegiatan Persiapan</Label>
                    <Select value={form.kegiatan_persiapan} onValueChange={(v) => setForm({ ...form, kegiatan_persiapan: v })}>
                      <SelectTrigger><SelectValue placeholder="Pilih kegiatan" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pengeringan">Pengeringan Kolam</SelectItem>
                        <SelectItem value="pengapuran">Pengapuran</SelectItem>
                        <SelectItem value="pengisian_air">Pengisian Air</SelectItem>
                        <SelectItem value="fermentasi">Fermentasi Air</SelectItem>
                        <SelectItem value="penumbuhan_plankton">Penumbuhan Plankton</SelectItem>
                        <SelectItem value="lainnya">Lainnya</SelectItem>
                      </SelectContent>
                    </Select></div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Kondisi Tambak</Label>
                    <Input placeholder="Misal: kolam sudah kering, pH tanah 7.0" value={form.kondisi_tambak} onChange={(e) => setForm({ ...form, kondisi_tambak: e.target.value })} /></div>
                </>)}

                {/* === TEBAR === */}
                {form.status === "tebar" && (<>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Tebar</Label>
                      <Input data-testid="input-tanggal-tebar" type="date" value={form.tanggal_tebar} onChange={(e) => setForm({ ...form, tanggal_tebar: e.target.value })} required /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah Benur</Label>
                      <Input data-testid="input-jumlah-benur" type="number" value={form.jumlah_benur} onChange={(e) => setForm({ ...form, jumlah_benur: parseInt(e.target.value) || 0 })} required /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Asal Benur / Hatchery</Label>
                      <Input data-testid="input-asal-benur" value={form.asal_benur} onChange={(e) => setForm({ ...form, asal_benur: e.target.value })} required /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Ukuran Benur (PL)</Label>
                      <Input placeholder="Misal: PL-10, PL-12" value={form.ukuran_benur} onChange={(e) => setForm({ ...form, ukuran_benur: e.target.value })} /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Harga Benur (Rp/ekor)</Label>
                    <Input type="number" step="0.1" value={form.harga_benur || ""} onChange={(e) => setForm({ ...form, harga_benur: parseFloat(e.target.value) || null })} /></div>
                </>)}

                {/* === PEMELIHARAAN === */}
                {form.status === "pemeliharaan" && (<>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Tebar</Label>
                      <Input type="date" value={form.tanggal_tebar} onChange={(e) => setForm({ ...form, tanggal_tebar: e.target.value })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah Benur</Label>
                      <Input type="number" value={form.jumlah_benur} onChange={(e) => setForm({ ...form, jumlah_benur: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Asal Benur</Label>
                    <Input value={form.asal_benur} onChange={(e) => setForm({ ...form, asal_benur: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Target Size (ekor/kg)</Label>
                      <Input type="number" step="0.1" value={form.target_size || ""} onChange={(e) => setForm({ ...form, target_size: parseFloat(e.target.value) || null })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Target Hari Panen</Label>
                      <Input type="number" value={form.target_hari || ""} onChange={(e) => setForm({ ...form, target_hari: parseInt(e.target.value) || null })} /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Metode Budidaya</Label>
                    <Select value={form.metode_budidaya} onValueChange={(v) => setForm({ ...form, metode_budidaya: v })}>
                      <SelectTrigger><SelectValue placeholder="Pilih metode" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="intensif">Intensif</SelectItem>
                        <SelectItem value="semi_intensif">Semi Intensif</SelectItem>
                        <SelectItem value="tradisional">Tradisional</SelectItem>
                        <SelectItem value="super_intensif">Super Intensif</SelectItem>
                      </SelectContent>
                    </Select></div>
                </>)}

                {/* === PANEN === */}
                {form.status === "panen" && (<>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Tebar (awal)</Label>
                      <Input type="date" value={form.tanggal_tebar} onChange={(e) => setForm({ ...form, tanggal_tebar: e.target.value })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Panen</Label>
                      <Input type="date" value={form.tanggal_panen} onChange={(e) => setForm({ ...form, tanggal_panen: e.target.value })} required /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah Benur Awal</Label>
                      <Input type="number" value={form.jumlah_benur} onChange={(e) => setForm({ ...form, jumlah_benur: parseInt(e.target.value) || 0 })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Asal Benur</Label>
                      <Input value={form.asal_benur} onChange={(e) => setForm({ ...form, asal_benur: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Estimasi Berat Panen (kg)</Label>
                      <Input type="number" step="0.1" value={form.estimasi_berat || ""} onChange={(e) => setForm({ ...form, estimasi_berat: parseFloat(e.target.value) || null })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Estimasi Size (ekor/kg)</Label>
                      <Input type="number" step="0.1" value={form.estimasi_size || ""} onChange={(e) => setForm({ ...form, estimasi_size: parseFloat(e.target.value) || null })} /></div>
                  </div>
                </>)}

                {/* === SELESAI === */}
                {form.status === "selesai" && (<>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Tanggal Tebar</Label>
                      <Input type="date" value={form.tanggal_tebar} onChange={(e) => setForm({ ...form, tanggal_tebar: e.target.value })} /></div>
                    <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Jumlah Benur</Label>
                      <Input type="number" value={form.jumlah_benur} onChange={(e) => setForm({ ...form, jumlah_benur: parseInt(e.target.value) || 0 })} /></div>
                  </div>
                  <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Asal Benur</Label>
                    <Input value={form.asal_benur} onChange={(e) => setForm({ ...form, asal_benur: e.target.value })} /></div>
                </>)}

                {/* Catatan (selalu tampil) */}
                <div className="space-y-2"><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-slate-600">Catatan</Label><Textarea placeholder={
                  form.status === "persiapan" ? "Catatan kondisi persiapan kolam..." :
                  form.status === "tebar" ? "Catatan proses penebaran benur..." :
                  form.status === "pemeliharaan" ? "Catatan pemeliharaan harian..." :
                  form.status === "panen" ? "Catatan proses panen..." :
                  "Evaluasi dan catatan akhir siklus..."
                } value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} /></div>
                <Button type="submit" data-testid="submit-siklus-btn" className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-white">Simpan</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

      <div className="relative w-full sm:w-72">
      </div>
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="search-siklus" placeholder="Cari siklus..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="pl-9 h-9" />
      </div>

      <Card className="bg-white border border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Tambak</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Tgl Tebar</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Jml Benur</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Asal</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Target</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Status</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Dibuat Oleh</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Memuat...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Belum ada siklus</TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s._id} className="hover:bg-slate-50">
                  <TableCell className="text-sm font-medium">{s.tambak_nama}</TableCell>
                  <TableCell className="text-sm">{s.tanggal_tebar}</TableCell>
                  <TableCell className="text-sm font-semibold">{s.jumlah_benur?.toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-sm">{s.asal_benur}</TableCell>
                  <TableCell className="text-sm">{s.target_size ? `${s.target_size}` : "-"}{s.target_hari ? ` / ${s.target_hari}hr` : ""}</TableCell>
                  <TableCell>{statusBadge(s.status)}</TableCell>
                  <TableCell className="text-sm text-slate-500">{s.created_by}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openMonitoring(s)} className="text-[#008080]" data-testid={`monitoring-${s._id}`} title="Monitoring Harian">
                        <TableIcon size={16} />
                      </Button>
                      {!isFieldRole && (
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(s)} data-testid={`edit-siklus-${s._id}`}>
                        <PencilSimple size={16} />
                      </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-4 pb-3"><Pagination page={page} limit={15} total={total} onPageChange={setPage} /></div>
        </CardContent>
      </Card>
    </div>
  );
}
